/* LogWearForPatientModal — iter98.99dy
 *
 * Lets a Patient Engagement Specialist record daily wear-time on the
 * patient's behalf after a phone call, in-app walkthrough, or in-person
 * visit. Designed for minors, low-digital-literacy adults, and patients
 * temporarily without device access.
 *
 *  - 7-day grid (oldest → newest, left → right). Each day card shows the
 *    existing value + provenance ("Patient", "You · phone call", etc.).
 *  - Click a day → reveals the editor (hours number, verification method
 *    pills, optional note). Save persists ONE day.
 *  - Toast confirms; the row refetches.
 *
 *  - Patient-entered logs are RENDER-ONLY here — we don't want the coach
 *    to accidentally overwrite the patient's self-reported ground truth.
 *    We surface them with a "Patient logged" badge instead and lock the
 *    inputs (coach can still click an empty or coach-entered day).
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    ClockUser, X, Check, Phone, DeviceMobileSpeaker, House, Info, PaperPlaneTilt,
} from "@phosphor-icons/react";


const VERIFICATION_METHODS = [
    { key: "phone_call",   label: "Phone call",     icon: Phone },
    { key: "in_app_assist", label: "In-app assist", icon: DeviceMobileSpeaker },
    { key: "in_person",    label: "In person",      icon: House },
];


function isoDateNDaysAgo(n) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - n);
    return d.toISOString().slice(0, 10);
}

function dayLabel(iso) {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("en-IN", { weekday: "short" });
}

function dayShort(iso) {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

// Compact numeric date for the 7-day pills, e.g. "24/06".
function dayNumeric(iso) {
    const d = new Date(iso + "T00:00:00Z");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}`;
}


export default function LogWearForPatientModal({ open, onClose, caseRow, onSaved }) {
    const [logs, setLogs] = useState([]);       // last 7 days from API
    const [loading, setLoading] = useState(true);
    const [activeDate, setActiveDate] = useState(null); // ISO date being edited
    const [hours, setHours] = useState("");
    const [verification, setVerification] = useState("phone_call");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    // Last 7 days, oldest first.
    const week = useMemo(
        () => Array.from({ length: 7 }, (_, i) => isoDateNDaysAgo(6 - i)),
        [],
    );
    const logByDate = useMemo(() => {
        const m = new Map();
        for (const l of logs) m.set(l.log_date, l);
        return m;
    }, [logs]);

    const load = async () => {
        if (!caseRow?.id) return;
        setLoading(true);
        try {
            const r = await api.get(`/coe/compliance/cases/${caseRow.id}/wear-log?days=7`);
            setLogs(r.data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't load wear-log");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        if (!open) return;
        setActiveDate(null);
        setHours("");
        setNote("");
        setVerification("phone_call");
        load();
    }, [open, caseRow?.id]);

    if (!open || !caseRow) return null;

    const startEdit = (iso) => {
        const existing = logByDate.get(iso);
        // Lock editing when the patient self-logged this day.
        if (existing && existing.logged_by_role === "patient") {
            toast.info("This day was logged by the patient. Edits are locked.");
            return;
        }
        setActiveDate(iso);
        setHours(existing ? String(existing.hours) : "");
        setVerification(existing?.verification_method || "phone_call");
        setNote(existing?.note || "");
    };

    const hoursNum = Number(hours);
    const canSave = activeDate && !Number.isNaN(hoursNum) && hoursNum >= 0 && hoursNum <= 24 && !saving;

    const save = async () => {
        if (!canSave) return;
        setSaving(true);
        try {
            await api.post(
                `/coe/compliance/cases/${caseRow.id}/wear-log`,
                {
                    log_date: activeDate,
                    hours: Number(hoursNum.toFixed(2)),
                    note: note.trim() || null,
                    verification_method: verification,
                },
            );
            toast.success(`Wear-time saved for ${dayShort(activeDate)}`);
            onSaved?.();
            await load();
            setActiveDate(null);
            setHours("");
            setNote("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't save");
        } finally { setSaving(false); }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
            onClick={onClose}
            data-testid="log-wear-modal"
        >
            <div
                className="w-full max-w-xl bg-card border border-border rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] min-h-[380px] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-5 pt-4 pb-3 border-b border-border flex items-start justify-between gap-3">
                    <div className="min-w-0 flex items-center gap-2">
                        <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
                            <ClockUser size={16} weight="duotone" />
                        </div>
                        <div className="min-w-0">
                            <div className="font-display text-base font-bold leading-tight truncate">
                                Log wear-time for {caseRow.patient_name || "patient"}
                            </div>
                            <div className="text-[11px] text-muted-foreground truncate">
                                {caseRow.code} · After a phone call, in-app walkthrough, or in-person visit.
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full grid place-items-center hover:bg-secondary text-muted-foreground transition-colors"
                        data-testid="log-wear-close"
                    >
                        <X size={14} weight="bold" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
                    {/* 7-day grid */}
                    <div>
                        <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground mb-2">
                            Last 7 days — pick a day to log
                        </div>
                        <div className="grid grid-cols-7 gap-1.5" data-testid="log-wear-week-grid">
                            {week.map((iso) => {
                                const log = logByDate.get(iso);
                                const isActive = activeDate === iso;
                                const isPatient = log?.logged_by_role === "patient";
                                const isCoach = log?.logged_by_role === "coach";
                                const empty = !log;
                                let cardCls = "border-border bg-card hover:border-primary/40";
                                if (isActive) cardCls = "border-primary ring-2 ring-primary/20 bg-primary/5";
                                else if (isPatient) cardCls = "border-emerald-200 bg-emerald-50/60 cursor-not-allowed";
                                else if (isCoach) cardCls = "border-violet-200 bg-violet-50/40 hover:border-violet-300";
                                else if (empty) cardCls = "border-dashed border-muted-foreground/30 bg-card hover:border-primary/40";
                                return (
                                    <button
                                        key={iso}
                                        type="button"
                                        onClick={() => startEdit(iso)}
                                        disabled={isPatient}
                                        className={`relative text-left rounded-lg border px-2 py-2 transition-all min-h-[70px] ${cardCls}`}
                                        data-testid={`log-wear-day-${iso}`}
                                    >
                                        <div className="text-[9.5px] uppercase font-bold tracking-wider text-muted-foreground flex items-baseline gap-1">
                                            <span>{dayLabel(iso)}</span>
                                            <span className="text-[9px] font-semibold tracking-normal tabular-nums text-muted-foreground/70 normal-case">
                                                {dayNumeric(iso)}
                                            </span>
                                        </div>
                                        <div className="font-display text-[13px] font-bold tabular-nums mt-0.5">
                                            {log ? `${log.hours}h` : "—"}
                                        </div>
                                        {isPatient && (
                                            <div className="text-[8.5px] font-bold uppercase tracking-wider text-emerald-700 mt-0.5">
                                                Patient
                                            </div>
                                        )}
                                        {isCoach && (
                                            <div className="text-[8.5px] font-bold uppercase tracking-wider text-violet-700 mt-0.5 truncate" title={log.logged_by_name}>
                                                You
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-1.5 text-[10.5px] text-muted-foreground flex items-center gap-1.5">
                            <Info size={11} weight="duotone" />
                            Days the patient already logged themselves are locked — their data is the source of truth.
                        </div>
                    </div>

                    {/* Editor (revealed when a day is selected). We reserve
                        enough vertical room in the modal shell that this
                        expanding block cannot yank the modal upward while
                        the outer flex re-centers. */}
                    {activeDate && (
                        <div className="rounded-xl border border-primary/30 bg-primary/[0.04] p-4 space-y-3" data-testid="log-wear-editor">
                            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-primary">
                                Editing · {dayShort(activeDate)}
                            </div>

                            {/* Hours */}
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground" htmlFor="hours-input">
                                    Hours worn (0-24)
                                </label>
                                <div className="mt-1 flex items-center gap-2">
                                    <input
                                        id="hours-input"
                                        type="number"
                                        min="0"
                                        max="24"
                                        step="0.5"
                                        value={hours}
                                        onChange={(e) => setHours(e.target.value)}
                                        placeholder="e.g. 21.5"
                                        className="w-28 px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15"
                                        data-testid="log-wear-hours-input"
                                    />
                                    <span className="text-[11px] text-muted-foreground">of 24 hrs · target: 22+ hrs</span>
                                </div>
                            </div>

                            {/* Verification method */}
                            <div>
                                <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
                                    How did you confirm?
                                </div>
                                <div className="flex flex-wrap gap-1.5" data-testid="log-wear-verification">
                                    {VERIFICATION_METHODS.map((m) => {
                                        const VIcon = m.icon;
                                        const active = verification === m.key;
                                        return (
                                            <button
                                                key={m.key}
                                                type="button"
                                                onClick={() => setVerification(m.key)}
                                                className={`inline-flex items-center gap-1.5 px-3 h-8 text-[11.5px] font-semibold rounded-full transition-all ${
                                                    active
                                                        ? "bg-primary text-primary-foreground shadow-sm"
                                                        : "bg-card border border-border text-muted-foreground hover:text-foreground"
                                                }`}
                                                data-testid={`log-wear-method-${m.key}`}
                                            >
                                                <VIcon size={11} weight={active ? "fill" : "duotone"} />
                                                {m.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Note */}
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground" htmlFor="note-input">
                                    Note (optional)
                                </label>
                                <textarea
                                    id="note-input"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    rows={2}
                                    placeholder="e.g. 'Mom called — Riya kept them in all day at school.'"
                                    className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15 resize-none"
                                    data-testid="log-wear-note-input"
                                />
                            </div>
                        </div>
                    )}

                    {/* Idle hint — ALWAYS rendered while no day is being
                        edited (even during the initial `loading` window)
                        so its height is reserved from frame 1. Hiding it
                        behind `!loading` was the source of the ~50px post-
                        mount growth that made the whole modal slide up
                        inside its flex-centered overlay. */}
                    {!activeDate && (
                        <div className="text-[12.5px] text-muted-foreground text-center py-2 min-h-[32px] flex items-center justify-center">
                            Tap a day above to enter or update wear-time.
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-border bg-secondary/20 flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 h-9 rounded-lg hover:bg-secondary transition-colors"
                        data-testid="log-wear-cancel-btn"
                    >
                        Close
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={!canSave}
                        className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        data-testid="log-wear-save-btn"
                    >
                        {saving ? <PaperPlaneTilt size={13} weight="bold" /> : <Check size={14} weight="bold" />}
                        {saving ? "Saving…" : "Save entry"}
                    </button>
                </div>
            </div>
        </div>
    );
}
